/**
 * トップページのスプラッシュ。
 * 0→100% のカウントアップ → 一拍ドクン → スプラッシュが上に剥がれ、
 * 同じ動きで .wrapper（ページ全体）がズームインして出てくる。
 *
 * - セッション中1回のみ発火（sessionStorage）
 * - prefers-reduced-motion / JS要素不足 / 2回目以降 は即終了（アニメ無し）
 * - 対象マークアップ [data-splash] は is_front_page() のとき header.php だけが出力
 *
 * 元ネタ: addict_intraction / src/demos/loader-splash-counter.astro（gsap版をWeb Animations APIで再現）
 */

import { getLenis } from "./lib-smooth-scroll"

/** sessionStorage キー */
const SPLASH_KEY = "splashed"
/** 1セルの高さ(em)。SCSS .splash__digit / __strip>* の 0.72em と一致させる */
const CELL_EM = 0.72
/** カウント尺(ms) */
const COUNT_MS = 3000
/** 100到達後のホールド(ms) */
const HOLD_MS = 150
/** 剥がれ尺(ms) */
const PEEL_MS = 1500
/** 剥がれ・ズームインのイージング（デモの peelEaseL） */
const PEEL_EASE = "cubic-bezier(0.75, 0, 0.2, 1)"

/**
 * 指定msだけ待つ
 * @param {number} ms - 待ち時間(ms)
 * @returns {Promise<void>}
 */
const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * easeInOutQuad（gsap power2.inOut 相当。中盤加速→終盤減速）
 * @param {number} t - 進捗 0〜1
 * @returns {number} イージング後の値 0〜1
 */
const easeInOutQuad = (t: number): number => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2)

/**
 * スクロールを無効化する（ロック中に登録）
 * @param {Event} event - イベント
 */
const disableScroll = (event: Event): void => {
  event.preventDefault()
}

/**
 * 10の位・1の位の strip を「0-9 × 11セット」(110要素) で構築する。
 * 連続値で線形スクロールさせるため（9→0 のジャンプが起きない）。
 * @param {HTMLElement} strip - 対象 strip
 * @param {number} sets - セット数
 */
const buildStrip = (strip: HTMLElement, sets = 11): void => {
  const frag = document.createDocumentFragment()
  for (let s = 0; s < sets; s++) {
    for (let d = 0; d < 10; d++) {
      const cell = document.createElement("div")
      cell.textContent = String(d)
      frag.appendChild(cell)
    }
  }
  strip.replaceChildren(frag)
}

/**
 * 1本の strip を連続値ぶんだけ translateY する
 * @param {HTMLElement} strip - 対象 strip
 * @param {number} value - 表示したい連続値
 */
const setStrip = (strip: HTMLElement, value: number): void => {
  strip.style.transform = `translateY(${-value * CELL_EM}em)`
}

/**
 * カウンターを n（連続値 0-100）に更新する。全桁とも連続値で滑らかにスクロールさせる。
 * 100の位は「最後の10（90→100）」だけ 0→1 へ他桁と同速でロールさせる
 * （中間で「1」が混じらず、かつ100到達が一瞬で切り替わらない）。
 * @param {HTMLElement[]} strips - [100の位, 10の位, 1の位]
 * @param {number} n - 連続値 0-100
 */
const updateCounter = (strips: HTMLElement[], n: number): void => {
  const c = Math.max(0, Math.min(n, 100))
  setStrip(strips[0], Math.min(Math.max((c - 90) / 10, 0), 1)) // 90→100 で 0→1 ロール
  setStrip(strips[1], c / 10) // 10の位: 0→10（連続）
  setStrip(strips[2], c) // 1の位: 0→100（連続）
}

/**
 * 0→100 をイージングでカウントアップする（rAF）
 * @param {HTMLElement[]} strips - 桁 strip 配列
 * @param {number} duration - 尺(ms)
 * @returns {Promise<void>} 完了で resolve
 */
const runCount = (strips: HTMLElement[], duration: number): Promise<void> =>
  new Promise((resolve) => {
    let start: number | null = null
    const step = (ts: number): void => {
      if (start === null) start = ts
      const p = Math.min((ts - start) / duration, 1)
      updateCounter(strips, easeInOutQuad(p) * 100)
      if (p < 1) {
        requestAnimationFrame(step)
      } else {
        updateCounter(strips, 100)
        resolve()
      }
    }
    requestAnimationFrame(step)
  })

/**
 * カウンター文字を一拍ドクンさせる（opacity/blur の心拍）
 * @param {HTMLElement} el - カウンター数字要素
 * @returns {Promise<void>}
 */
const heartbeat = (el: HTMLElement): Promise<void> => {
  const anim = el.animate(
    [
      { opacity: 1, filter: "blur(0px)", offset: 0, easing: "cubic-bezier(0.55, 0, 1, 0.45)" }, // power2.in
      { opacity: 0.6, filter: "blur(4px)", offset: 0.5625, easing: "cubic-bezier(0.16, 1, 0.3, 1)" }, // expo.out
      { opacity: 1, filter: "blur(0px)", offset: 1 },
    ],
    { duration: 320, fill: "both" }
  )
  return anim.finished.then(() => undefined).catch(() => undefined)
}

/**
 * FV動画(.hero__video ＝本編＋上部反射--top)を頭出しして再生する。
 * 動画は autoplay で読込と同時に再生開始しており、スプラッシュのカウント中(約3.5秒)に進んでしまう。
 * 剥がれ＝動画が現れる瞬間にこれを呼び、再生位置を0へ戻して「最初から」始める。
 * @returns {void}
 */
const restartHeroVideo = (): void => {
  document.querySelectorAll<HTMLVideoElement>(".hero__video").forEach((video) => {
    video.currentTime = 0
    const played = video.play()
    if (played) played.catch(() => undefined) // muted想定で通るが、拒否されても無視
  })
}

/**
 * 剥がれ演出：スプラッシュが上へ、.wrapper が同じ動きでズームイン、中盤に控えめ暗転。
 * @param {HTMLElement} splash - スプラッシュ要素
 * @param {HTMLElement} wrapper - ページ全体ラッパー
 * @param {HTMLElement | null} veil - 暗転ヴェール
 * @returns {{ finished: Promise<void>, wrapperAnim: Animation }}
 */
const peel = (
  splash: HTMLElement,
  wrapper: HTMLElement,
  veil: HTMLElement | null
): { finished: Promise<void>; wrapperAnim: Animation } => {
  const splashAnim = splash.animate(
    [
      { transform: "translateY(0%)", filter: "blur(0px)", opacity: 1 },
      { transform: "translateY(-100%)", filter: "blur(6px)", opacity: 0.85 },
    ],
    { duration: PEEL_MS, easing: PEEL_EASE, fill: "both" }
  )

  // .wrapper はページ全高（超縦長）なので % / center 基準だと桁違いにズレる。
  // スクロールは0固定なので「画面中央(50vh)」を原点にし、移動量も vh で指定する。
  wrapper.style.transformOrigin = "50% 50vh"
  wrapper.style.willChange = "transform, filter, opacity"
  const wrapperAnim = wrapper.animate(
    [
      // 奥（縮小・ぼけ・暗・半透明・少し下）から
      { transform: "translateY(8vh) scale(0.82)", filter: "blur(12px) brightness(0.5)", opacity: 0.6 },
      // 手前（定位置・クリア・明・不透明）へ浮かび上がる
      { transform: "translateY(0) scale(1)", filter: "blur(0px) brightness(1)", opacity: 1 },
    ],
    { duration: PEEL_MS, easing: PEEL_EASE, fill: "both" }
  )

  if (veil) {
    veil.animate(
      [
        { opacity: 0, offset: 0 },
        { opacity: 0.3, offset: 0.4, easing: "cubic-bezier(0, 0, 0.58, 1)" },
        { opacity: 0, offset: 1 },
      ],
      { duration: PEEL_MS, fill: "both" }
    )
  }

  return { finished: splashAnim.finished.then(() => undefined).catch(() => undefined), wrapperAnim }
}

/**
 * スプラッシュを終了し、通常状態へ戻す
 * @param {HTMLElement | null} splash - スプラッシュ要素
 * @param {HTMLElement | null} veil - 暗転ヴェール
 * @param {HTMLElement} wrapper - ページ全体ラッパー
 * @param {Animation} [wrapperAnim] - ズームインのアニメーション（あれば解除）
 */
const endSplash = (
  splash: HTMLElement | null,
  veil: HTMLElement | null,
  wrapper: HTMLElement,
  wrapperAnim?: Animation
): void => {
  document.body.classList.remove("splash-is-on")
  document.body.classList.add("splashed")
  document.removeEventListener("touchmove", disableScroll)
  document.removeEventListener("wheel", disableScroll)
  wrapperAnim?.cancel() // 最終状態＝無変形なので解除しても見た目は変わらない（will-change解放のため）
  wrapper.style.willChange = ""
  wrapper.style.transformOrigin = ""
  splash?.remove()
  veil?.remove()
}

/**
 * スプラッシュ一連のシーケンスを実行する
 * @param {HTMLElement} splash
 * @param {HTMLElement | null} veil
 * @param {HTMLElement} wrapper
 * @param {HTMLElement} numEl - カウンター数字要素
 * @param {HTMLElement[]} strips
 * @returns {Promise<void>}
 */
const runSequence = async (
  splash: HTMLElement,
  veil: HTMLElement | null,
  wrapper: HTMLElement,
  numEl: HTMLElement,
  strips: HTMLElement[]
): Promise<void> => {
  await runCount(strips, COUNT_MS)
  await wait(HOLD_MS)
  await heartbeat(numEl)
  restartHeroVideo() // 剥がれ＝動画が現れる瞬間にFV動画を頭出し（0秒から再生）
  const { finished, wrapperAnim } = peel(splash, wrapper, veil)
  await finished
  endSplash(splash, veil, wrapper, wrapperAnim)
}

/**
 * スプラッシュを初期化する
 */
const init = (): void => {
  const splash = document.querySelector<HTMLElement>("[data-splash]")
  if (!splash) return // トップページ以外は要素が無い

  const veil = document.querySelector<HTMLElement>("[data-splash-veil]")
  const wrapper = document.querySelector<HTMLElement>(".wrapper")
  const numEl = splash.querySelector<HTMLElement>("[data-splash-num]")
  const strips = Array.from(splash.querySelectorAll<HTMLElement>("[data-splash-strip]"))
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  const alreadySplashed = sessionStorage.getItem(SPLASH_KEY)

  // 2回目以降 / reduced-motion / 要素不足 → アニメ無しで即終了
  if (alreadySplashed || reduceMotion || !wrapper || !numEl || strips.length !== 3) {
    splash.remove()
    veil?.remove()
    document.body.classList.add("splashed")
    return
  }

  sessionStorage.setItem(SPLASH_KEY, "true")
  document.body.classList.add("splash-is-on")
  // Lenis有効時は lenis.scrollTo（ネイティブ scrollTo との二重制御を防ぐ）
  const lenis = getLenis()
  if (lenis) lenis.scrollTo(0, { immediate: true })
  else window.scrollTo(0, 0)
  document.addEventListener("touchmove", disableScroll, { passive: false })
  document.addEventListener("wheel", disableScroll, { passive: false })

  buildStrip(strips[1])
  buildStrip(strips[2])
  updateCounter(strips, 0)

  void runSequence(splash, veil, wrapper, numEl, strips)
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init)
} else {
  init()
}
